import { type FeedbackType, getFeedbackDescription, getFeedbackLabel } from "@serophin/nottldr-shared";
import { useArticleFeedback } from "@serophin/nottldr-shared/hooks";
import { Brain, Eye, type LucideIcon, Sparkles, Wrench } from "lucide-preact";
import { useState } from "preact/hooks";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { useToast } from "../../contexts/ToastContext";
import { useIsSignedIn } from "../../platform/session";
import { ReportArticleModal } from "./ReportArticleModal";

type FeedbackOption = { id: FeedbackType; icon: LucideIcon };

const OPTIONS: FeedbackOption[] = [
  { id: "tp", icon: Brain },
  { id: "eo", icon: Eye },
  { id: "pr", icon: Wrench },
  { id: "in", icon: Sparkles },
];

export type ArticleFeedbackProps = {
  articleId: string;
  authorAlias: string;
};

type OptionButtonProps = { option: FeedbackOption; isSelected: boolean; isBusy: boolean; onPick: () => void };

const OptionButton = ({ option, isSelected, isBusy, onPick }: OptionButtonProps) => {
  const Icon = option.icon;
  return (
    <button
      type="button"
      data-testid={`feedback-${option.id}`}
      data-feedback-selected={isSelected}
      onClick={onPick}
      disabled={isBusy}
      class={`flex flex-col px-3 py-2.5 rounded-lg transition-all duration-200 text-left border ${
        isSelected
          ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300"
          : "bg-neutral-50 dark:bg-neutral-800/50 text-gray-600 dark:text-gray-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 border-neutral-200 dark:border-neutral-700"
      } ${isBusy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div class="flex items-center mb-1.5">
        <Icon size={16} class={`flex-shrink-0 mr-2 ${isSelected ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-500"}`} />
        <div class={`font-medium text-xs ${isSelected ? "text-blue-700 dark:text-blue-300" : "text-gray-700 dark:text-gray-300"}`}>
          {getFeedbackLabel(option.id)}
        </div>
      </div>
      <div class={`text-xs leading-snug ${isSelected ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"}`}>
        {getFeedbackDescription(option.id)}
      </div>
    </button>
  );
};

export const ArticleFeedback = ({ articleId, authorAlias }: ArticleFeedbackProps) => {
  const { showAuthModal } = useAuthModal();
  const toast = useToast();
  const signedIn = useIsSignedIn();
  const { feedbackType, toggle } = useArticleFeedback(authorAlias, articleId, signedIn);
  const [isBusy, setIsBusy] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const pick = async (type: FeedbackType) => {
    if (!signedIn) {
      showAuthModal("Sign in to give feedback", "Create an account or sign in to share your thoughts");
      return;
    }
    setIsBusy(true);
    try {
      await toggle(type);
    } catch {
      toast.error("Failed to update feedback. Please try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const report = () => {
    if (signedIn) {
      setShowReport(true);
    } else {
      showAuthModal("Sign in to report", "Create an account or sign in to report articles");
    }
  };

  return (
    <>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">How was this article?</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        {OPTIONS.map((option) => (
          <OptionButton key={option.id} option={option} isSelected={feedbackType === option.id} isBusy={isBusy} onPick={() => pick(option.id)} />
        ))}
      </div>
      <div class="mt-3 text-center">
        <span class="text-xs text-gray-500 dark:text-gray-400">
          <button type="button" onClick={report} class="underline">
            Report
          </button>{" "}
          this article
        </span>
      </div>
      <ReportArticleModal isOpen={showReport} onClose={() => setShowReport(false)} articleId={articleId} authorAlias={authorAlias} />
    </>
  );
};
